import { useCallback, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import { executePostAccountIdentityVerifySubmit } from '../../dataAccess/webApi/dao/accountDao'
import { accountIdentityVerifySubmitCategory } from '../../dataAccess/webApi/dto/accountDto'
import { translate } from '../../i18n'
import { getSessionAccessToken, verifyTel } from '../../utils/authUtil'
import { getNow } from '../../utils/dateUtil'
import { useErrorHandle } from '../common/error/errorHandler'
import { notifyMessage, showLoading } from '../common/store/slices/application'
import { detectLoginStatus, selectIsVerifiedTel } from '../common/store/slices/authority'
import { selectSystemControl } from '../common/store/slices/systemControl'
import { yesNo } from '../common/constant/classification'
import {
  identityVerificationUrl,
  emailVerifiedUrl,
} from '../common/constant/appUrl'
import { executeGetAccountOtp } from '../../dataAccess/webApi/dao/accountDao'

interface LocationState {
  from: Location
}

interface Inputs {
  oneTimePassword: string
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation<LocationState>()
  const isVerifiedTel = useSelector(selectIsVerifiedTel)
  const { smsSubscriptionFlag, phoneCertificationFlag } = useSelector(selectSystemControl)
  
  const [identifier, setIdentifier] = useState<Date>(getNow())
  const [resultCode, setResultCode] = useState<{ verifySubmit?: number; resend?: number }>()

  const formMethods = useForm<Inputs>()

  const from = location.state?.from

  const resend = useCallback(() => {
    setResultCode({})

    dispatch(
      showLoading({
        process: errorHandle(async () => {
          try {
            await verifyTel()
            dispatch(notifyMessage(translate('system.success.resendOneTimePassword')))
            setIdentifier(getNow())
          } catch (e) {
            if (e instanceof Error && (e.name === 'LimitExceededException' || e.name === 'TooManyRequestsException')) {
              setResultCode({ resend: 3 })
            }
          }
        }),
        isHiddenMain: false,
      })
    )
  }, [dispatch, errorHandle])

  const onSubmit = useCallback(
    (data: Inputs) => {
      setResultCode({})

      dispatch(
        showLoading({
          process: errorHandle(async () => {
            const resp = await executePostAccountIdentityVerifySubmit({
              accessToken: await getSessionAccessToken(),
              category: accountIdentityVerifySubmitCategory.tel,
              passcode: data.oneTimePassword,
            })
            if (resp.resultCode) {
              // 検証失敗時
              setResultCode({ verifySubmit: resp.resultCode })
            } else {
              // 検証成功時
              await dispatch(detectLoginStatus())
              history.replace(emailVerifiedUrl.url(), { from: identityVerificationUrl.url() })
            }
          }),
          isHiddenMain: false,
        })
      )
    },
    [dispatch, errorHandle, formMethods]
  )

  const initialize = async () => {
    // auth_logからSMS認証状態を取得
    const authLog = await executeGetAccountOtp()
    const telVerifiedFlag = authLog.result.telVerifiedFlag

    if (telVerifiedFlag === yesNo.yes && smsSubscriptionFlag === yesNo.yes && phoneCertificationFlag === yesNo.yes) {
      return
    }

    const sendCode = async () => {
      // 電話番号検証用のワンタイムパスワード発行
      try {
        await verifyTel()
        dispatch(notifyMessage(translate('system.success.sendOneTimePassword')))
      } catch (e) {
        if (e instanceof Error && (e.name === 'LimitExceededException' || e.name === 'TooManyRequestsException')) {
          setResultCode({ resend: 3 })
        }
      }
    }
  
    if (telVerifiedFlag === yesNo.no && smsSubscriptionFlag === yesNo.yes && phoneCertificationFlag === yesNo.yes) {
      // SMS認証未済かつSMS認証を利用する場合
      sendCode()
    }
  }

  useEffect(() => {
    if (smsSubscriptionFlag === yesNo.no || phoneCertificationFlag === yesNo.no) {
      return
    }

    initialize()
  }, [])

  return {
    formMethods,
    isVerifiedTel,
    identifier,
    from,
    resend,
    onSubmit,
    smsSubscriptionFlag,
    phoneCertificationFlag,
    resultCode,
  }
}
